/**
 * Formats input values into a list of CLI args. Inverse of
 * parseArgsToInputValues: positionals are emitted first (ordered by
 * position), followed by optional flags. Each value is stringified so
 * that re-parsing through parseArgsToInputValues round-trips.
 *
 * @param {Object<string, {
 *   position: number|null,
 *   type: "string"|"float"|"int"|"boolean",
 *   nargs: number|"+"|"?"|null,
 *   value: *,
 *   argName: string,
 * }>} moduleVersionInputs
 * @param {Object<string, *>} inputValues - map from input key to its
 *     current value (job-level slice of the form state)
 * @returns {string[]}
 */
export function formatInputValuesToArgs(moduleVersionInputs, inputValues) {
    const entries = Object.entries(moduleVersionInputs);

    const positionalEntries = entries
        .filter(([, input]) => input.position != null)
        .sort(([, a], [, b]) => a.position - b.position);
    const optionalEntries = entries.filter(
        ([, input]) => input.position == null
    );

    const positionalArgs = positionalEntries.flatMap(([key, input]) =>
        formatPositional(key, input, inputValues[key])
    );
    const optionalArgs = optionalEntries.flatMap(([key, input]) =>
        formatOptional(key, input, inputValues[key])
    );

    return [...positionalArgs, ...optionalArgs];
}

/**
 * @param {string} key
 * @param {{nargs: number|"+"|null}} input
 * @param {*} value
 * @returns {string[]}
 */
function formatPositional(key, input, value) {
    if (value == null) {
        throw new Error(`Missing value for positional input: "${key}"`);
    }

    if (input.nargs === "+") {
        if (!Array.isArray(value) || value.length < 1) {
            throw new Error(
                `Expected non-empty array for nargs "+" input: "${key}"`
            );
        }
        return value.map(String);
    }
    if (input.nargs == null) {
        return [String(value)];
    }
    if (typeof input.nargs === "number") {
        if (!Array.isArray(value) || value.length !== input.nargs) {
            throw new Error(
                `Expected array of length ${input.nargs} for input: "${key}"`
            );
        }
        if (value.some((v) => v == null)) {
            throw new Error(
                `Positional input "${key}" has null element(s); all ${input.nargs} values are required`
            );
        }
        return value.map(String);
    }
    throw new Error(`Unimplemented nargs value: "${input.nargs}"`);
}

/**
 * For nargs "?", an unset value (null/undefined) is skipped, and a `true`
 * value emits the flag bare (e.g. "--verbose") to mirror the parser's
 * convention of coercing a bare flag to true.
 *
 * @param {string} key
 * @param {{argName: string, nargs: number|"+"|"?"|null}} input
 * @param {*} value
 * @returns {string[]}
 */
function formatOptional(key, input, value) {
    if (input.nargs === "?") {
        if (value == null) return [];
        if (value === true) return [input.argName];
        return [input.argName, String(value)];
    }

    if (value == null) return [];

    if (input.nargs === "+") {
        if (!Array.isArray(value) || value.length < 1) {
            throw new Error(
                `Expected non-empty array for nargs "+" input: "${key}"`
            );
        }
        return [input.argName, ...value.map(String)];
    }
    if (input.nargs == null) {
        return [input.argName, String(value)];
    }
    if (typeof input.nargs === "number") {
        if (!Array.isArray(value) || value.length !== input.nargs) {
            throw new Error(
                `Expected array of length ${input.nargs} for input: "${key}"`
            );
        }
        if (value.every((v) => v == null)) return [];
        if (value.some((v) => v == null)) {
            throw new Error(
                `Optional input "${key}" has null element(s); either clear all ${input.nargs} values or fill all`
            );
        }
        return [input.argName, ...value.map(String)];
    }
    throw new Error(`Unimplemented nargs value: "${input.nargs}"`);
}
